import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import InnerTable from "lib/Tables/InnerTable"; // Assuming this is your table component
import { Edit, Delete, Save, Add, Settings, Close } from "@mui/icons-material";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from "@mui/material";
import { get, post, request } from "requests";
import ManageStipulationComponent from "./ManageStipulationComponent";
import RequestStipulationComponent from "./RequestStipulationComponent";

interface Stipulation {
  id: number;
  name: string;
  instructions: string;
  note?: string;
  type?: string;
  default?: boolean;
}

interface StipulationsProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const Stipulations: React.FC<StipulationsProps> = ({ dealApplicationId, setComponentLoading }) => {
  const [stipulationsData, setStipulationsData] = useState<Stipulation[]>([]);
  const [openManageStipsDialog, setOpenManageStipsDialog] = useState(false);
  const [applicationStips, setApplicationStips] = useState<any[]>([]);
  const [editedNotes, setEditedNotes] = useState<Record<number, string>>({});
  const [showAdd, setShowAdd] = useState(false);
  const [showRequestStips, setShowRequestStips] = useState(false);
  const [showManageStips, setShowManageStips] = useState(false);

  useEffect(() => {
    if (dealApplicationId) {
      getAllStipulationForApplication();
    }
  }, [dealApplicationId]);

  const getAllStipulationForApplication = async () => {
    try {
      setComponentLoading("stipulations", true);
      const response = await get(`application/getAllStipulations/${dealApplicationId}`);
      if (response) {
        setApplicationStips(response || []);
        setStipulationsData(response?.stipulationDetails || []);
      } else {
        setApplicationStips([]);
        setStipulationsData([]);
      }
    } catch (error) {
      console.error("Error fetching stipulations:", error);
    } finally {
      setComponentLoading("stipulations", false);
    }
  };

  const deleteStipulationForApplication = async (stipulationId: string) => {
    if (!stipulationId || !dealApplicationId) return;
    try {
      const requestData = {
        applicationId: dealApplicationId,
        stipulationId: stipulationId,
      };
      await request("delete", "application/deleteStipulation", requestData);
      await getAllStipulationForApplication();
    } catch (error) {
      console.error("Error deleting stipulation:", error);
    }
  };

  const updateStipulationNote = (stipulationId: number, note: string) => {
    if (!stipulationId || !dealApplicationId) return;
    const requestData = {
      applicationId: dealApplicationId,
      stipId: stipulationId,
      note: note || "",
    };
    post(`application/updateStipulationNote`, requestData).then(() =>
      getAllStipulationForApplication()
    );
  };

  const handleEditNote = (stipId: number) => {
    const stip = stipulationsData.find((stip) => stip?.id === stipId);
    if (stip) {
      setEditedNotes({ ...editedNotes, [stipId]: stip.note || "" });
    }
  };

  const handleSaveNote = (stipId: number) => {
    updateStipulationNote(stipId, editedNotes[stipId]);
    const updatedEditedNotes = { ...editedNotes };
    delete updatedEditedNotes[stipId];
    setEditedNotes(updatedEditedNotes);
  };

  const handleCancelEdit = (stipId: number) => {
    const updatedEditedNotes = { ...editedNotes };
    delete updatedEditedNotes[stipId];
    setEditedNotes(updatedEditedNotes);
  };

  const handleDelete = (id: number) => {
    if (id) deleteStipulationForApplication(id.toString());
  };

  const columns = [
    {
      Header: <span style={{ fontSize: "12px", fontWeight: "bold" }}>Name</span>,
      accessor: "type",
      width: 150,
    },
    {
      Header: <span style={{ fontSize: "12px", fontWeight: "bold" }}>Instructions</span>,
      accessor: "note",
      Cell: ({ row }: { row: any }) => {
        const stipId = row?.original.id;
        if (stipId === undefined) return null;

        const isEditing = editedNotes[stipId] !== undefined;

        return (
          <MDBox>
            {isEditing ? (
              <MDBox display="flex" gap={2} alignItems={"center"}>
                <TextField
                  value={editedNotes[stipId]}
                  onChange={(e) => setEditedNotes({ ...editedNotes, [stipId]: e.target.value })}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                <Save onClick={() => handleSaveNote(stipId)} />
                <Close onClick={() => handleCancelEdit(stipId)} />
              </MDBox>
            ) : (
              <MDBox display="flex" justifyContent="space-between" gap={2} alignItems={"center"}>
                <span>{row.original.note}</span>
                <Edit onClick={() => handleEditNote(stipId)} />
              </MDBox>
            )}
          </MDBox>
        );
      },
      width: 300,
    },
    {
      Header: (
        <span style={{ fontSize: "12px", fontWeight: "bold", textAlign: "left" }}>Actions</span>
      ),
      accessor: "actions",
      Cell: ({ row }: { row: any }) => {
        return (
          <MDBox display={"flex"} justifyContent={"flex-start"}>
            <MDButton
              variant="text"
              color="error"
              onClick={() => handleDelete(row.original.id)}
              startIcon={<Delete sx={{ marginRight: "-4px" }} />}
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                padding: "4px 8px",
                marginLeft: "-6px",
              }}
            >
              Delete
            </MDButton>
          </MDBox>
        );
      },
      width: 100,
    },
  ];

  const renderTable = () => {
    return (
      <InnerTable
        table={{
          columns,
          rows: stipulationsData,
        }}
        paginationEnabled={false}
        fontSize={"0.75rem"}
      />
    );
  };

  return (
    <>
      <CollapsibleCard
        header={"Stipulations"}
        customButton={
          <MDButton
            color="primary"
            variant="contained"
            size="medium"
            sx={{ textTransform: "none" }}
            onClick={() => setShowRequestStips(true)}
          >
            + Add Stipulation
          </MDButton>
        }
      >
        <MDBox paddingRight={3}>{renderTable()}</MDBox>
      </CollapsibleCard>

      {/* Render RequestStipulationComponent */}
      {showRequestStips && (
        <RequestStipulationComponent
          applicationId={dealApplicationId}
          showRequestStipulations={showRequestStips}
          setShowStips={setShowRequestStips}
          setShowManageStips={setShowManageStips}
          refreshStipulationsforApp={getAllStipulationForApplication}
        />
      )}

      {/* Render ManageStipulationComponent */}
      {showManageStips && (
        <ManageStipulationComponent
          showManageStips={showManageStips}
          setShowManageStips={setShowManageStips}
          refreshAppStipulation={getAllStipulationForApplication}
        />
      )}

      {/* Dialog for managing stipulations */}
      <Dialog
        open={openManageStipsDialog}
        onClose={() => setOpenManageStipsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center">
              <Settings sx={{ mr: 1 }} />
              <Typography variant="h5">MANAGE STIPULATIONS</Typography>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <DialogContent style={{ paddingTop: 0 }}>
          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
            {!showAdd && (
              <IconButton onClick={() => setShowAdd(true)}>
                <Add style={{ fontSize: "25px", fontWeight: "bold", cursor: "pointer" }} />
              </IconButton>
            )}
          </div>
          <div style={{ marginTop: "10px" }}>
            {showAdd && (
              <div>
                <Typography variant="body1" style={{ fontSize: "15px" }}>
                  Add New Stipulation Form
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <MDButton
            color="error"
            variant="contained"
            onClick={() => setOpenManageStipsDialog(false)}
            style={{ marginRight: "10px" }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Stipulations;
